import { ParticipantType } from '@modusign/openapi-client-template/model';
import { FieldNode, FieldNodeType } from '@modusign/react-signing/types';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { DeepReadonly } from 'ts-essentials';

import { ConfigurableCheckboxFieldComponent } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/FieldRenderers/ConfigurableCheckboxGroupFieldRenderer/ConfigurableCheckboxFieldRenderer';
import { FieldComponent } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/FieldRenderers/fields';
import { countFields } from '@app/features/apply-react-signing/libs/countFields';
import getTextFelidSuggestions from '@app/features/apply-react-signing/libs/getTextFelidSuggestions';

type EvaluatedValueBase = {
  isDataLabelDuplicated: boolean;
};

type CreateEvaluatedValueType<T extends FieldNodeType, P extends ParticipantType, K = object> = {
  type: T;
  participantType: P;
} & K &
  EvaluatedValueBase;

type EvaluatedField =
  | CreateEvaluatedValueType<'TEXT', 'SIGNER'>
  | CreateEvaluatedValueType<'DROPDOWN', 'SIGNER'>
  | CreateEvaluatedValueType<
      'AUTOFILL_GROUP',
      'SIGNER',
      { dataLabelDuplicationRecords: Record<string, number> }
    >
  | CreateEvaluatedValueType<'SIGNATURE', 'SIGNER', { index: number }>
  | CreateEvaluatedValueType<'DATE', 'SIGNER'>
  | CreateEvaluatedValueType<'SIGNING_DATE', 'SIGNER'>
  | CreateEvaluatedValueType<'CHECKBOX_GROUP', 'SIGNER', { totalFieldCount: number }>
  | CreateEvaluatedValueType<'TEXT', 'REQUESTER', { fieldNumber: number; suggestions: string[] }>
  | CreateEvaluatedValueType<'CHECKBOX', 'REQUESTER', { fieldNumber: number }>
  | CreateEvaluatedValueType<'IMAGE', 'REQUESTER'>;

interface UseFieldsOptimization {
  evaluated: EvaluatedField;
  removeField: (id: string) => void;
  changeFields: (updater: (items: FieldNode[]) => FieldNode[]) => void; // (updater: UpdaterFunction<FieldNode>) => void
}

const OptimalFields = createContext<UseFieldsOptimization>({
  evaluated: {
    participantType: 'SIGNER',
    type: 'SIGNATURE',
    isDataLabelDuplicated: false,
    index: 0,
  },
  removeField: noop,
  changeFields: noop,
});

export function useOptimalFieldsContext<T extends FieldNodeType, P extends ParticipantType>(): {
  evaluated: Extract<EvaluatedField, { type: T; participantType: P }>;
} & UseFieldsOptimization {
  const context = useContext(OptimalFields);
  const result = useMemo(() => {
    return {
      evaluated: context.evaluated as Extract<EvaluatedField, { type: T; participantType: P }>,
      changeFields: context.changeFields,
      removeField: context.removeField,
    };
  }, [context.changeFields, context.evaluated, context.removeField]);
  return result;
}

function isDataLabelDuplication(
  field: DeepReadonly<FieldNode>,
  dataLabelDuplicationRecords: Record<string, number>
) {
  return dataLabelDuplicationRecords[field.dataLabel] > 1;
}

function createEvaluatedValue(
  id: string,
  fields: DeepReadonly<FieldNode[]>,
  dataLabelDuplicationRecords: Record<string, number>,
  fieldRecord: Record<string, DeepReadonly<FieldNode>>
): EvaluatedField {
  const field = fieldRecord[id];

  if (field) {
    if (field.type === 'CHECKBOX_GROUP' && field.participantReference.type === 'SIGNER') {
      const totalFieldCount = countFields(fields);

      return {
        type: 'CHECKBOX_GROUP',
        participantType: 'SIGNER',
        isDataLabelDuplicated: isDataLabelDuplication(field, dataLabelDuplicationRecords),
        totalFieldCount,
      };
    }

    if (field.type === 'CHECKBOX' && field.participantReference.type === 'REQUESTER') {
      return {
        type: 'CHECKBOX',
        participantType: 'REQUESTER',
        isDataLabelDuplicated: isDataLabelDuplication(field, dataLabelDuplicationRecords),
        fieldNumber: fields.findIndex((f) => f.id === field.id) + 1,
      };
    }

    if (field.type === 'SIGNATURE' && field.participantReference.type === 'SIGNER') {
      return {
        type: 'SIGNATURE',
        participantType: 'SIGNER',
        isDataLabelDuplicated: isDataLabelDuplication(field, dataLabelDuplicationRecords),
        index: fields
          .filter(
            (f) =>
              f.participantReference.id === field?.participantReference.id && f.type === 'SIGNATURE'
          )
          .findIndex((f) => f.id === field.id),
      };
    }

    if (field.type === 'TEXT' && field.participantReference.type === 'SIGNER') {
      return {
        type: 'TEXT',
        participantType: 'SIGNER',
        isDataLabelDuplicated: isDataLabelDuplication(field, dataLabelDuplicationRecords),
      };
    }

    if (field.type === 'TEXT' && field.participantReference.type === 'REQUESTER') {
      return {
        type: 'TEXT',
        participantType: 'REQUESTER',
        fieldNumber:
          fields
            .filter((f) => f.participantReference.type === 'REQUESTER')
            .findIndex((f) => f.id === field.id) + 1,
        isDataLabelDuplicated: isDataLabelDuplication(field, dataLabelDuplicationRecords),
        suggestions: getTextFelidSuggestions(field, fields),
      };
    }

    if (field.type === 'DROPDOWN' && field.participantReference.type === 'SIGNER') {
      return {
        type: 'DROPDOWN',
        isDataLabelDuplicated: isDataLabelDuplication(field, dataLabelDuplicationRecords),
        participantType: 'SIGNER',
      };
    }

    if (field.type === 'DATE' && field.participantReference.type === 'SIGNER') {
      return {
        type: 'DATE',
        isDataLabelDuplicated: isDataLabelDuplication(field, dataLabelDuplicationRecords),
        participantType: 'SIGNER',
      };
    }

    if (field.type === 'SIGNING_DATE' && field.participantReference.type === 'SIGNER') {
      return {
        type: 'SIGNING_DATE',
        isDataLabelDuplicated: isDataLabelDuplication(field, dataLabelDuplicationRecords),
        participantType: 'SIGNER',
      };
    }

    if (field.type === 'AUTOFILL_GROUP' && field.participantReference.type === 'SIGNER') {
      return {
        type: 'AUTOFILL_GROUP',
        isDataLabelDuplicated: isDataLabelDuplication(field, dataLabelDuplicationRecords),
        participantType: 'SIGNER',
        dataLabelDuplicationRecords,
      };
    }

    if (field.type === 'IMAGE' && field.participantReference.type === 'REQUESTER') {
      return {
        type: 'IMAGE',
        isDataLabelDuplicated: isDataLabelDuplication(field, dataLabelDuplicationRecords),
        participantType: 'REQUESTER',
      };
    }
  }
  return {
    participantType: 'SIGNER',
    type: 'SIGNATURE',
    index: 0,
    isDataLabelDuplicated: false,
  };
}

interface ProviderProps {
  id: string;
  children: ReactNode;
  fields: FieldNode[];
  dataLabelDuplicationRecords: Record<string, number>;
  fieldRecord: Record<string, FieldNode>;
  changeFields: (updater: (items: FieldNode[]) => FieldNode[]) => void; // (updater: UpdaterFunction<FieldNode>) => void
  removeField: (id: string) => void;
}

export const OptimalFieldsProvider = ({
  id,
  children,
  fields,
  dataLabelDuplicationRecords,
  fieldRecord,
  changeFields,
  removeField,
}: ProviderProps) => {
  const [cache, setCache] = useState<EvaluatedField>(
    createEvaluatedValue(id, fields, dataLabelDuplicationRecords, fieldRecord)
  );
  useEffect(() => {
    const next = createEvaluatedValue(id, fields, dataLabelDuplicationRecords, fieldRecord);
    if (!isEqual(cache, next)) {
      setCache(next);
    }
  }, [cache, dataLabelDuplicationRecords, fieldRecord, fields, id]);

  const value = useMemo<UseFieldsOptimization>(() => {
    return {
      evaluated: cache,
      changeFields,
      removeField,
    };
  }, [cache, changeFields, removeField]);

  return <OptimalFields.Provider value={value}> {children}</OptimalFields.Provider>;
};

export function withFieldRendererMemo(
  Component: FieldComponent | ConfigurableCheckboxFieldComponent
) {
  return React.memo(Component, (prev, current) => {
    return (
      prev.fieldId === current.fieldId &&
      prev.scale === current.scale &&
      prev.readOnly === current.readOnly &&
      prev.restrictModification === current.restrictModification
    );
  });
}
